import type React from 'react';
import { createContext, useContext, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  InteractionStatus,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import type { AuthUser, AuthState } from 'types/auth';
import { AppRole, AuthRole } from 'types/auth';
import { msalConfig, loginRequest, silentRequest, isMsalConfigured, apiScopes, msalInstance } from 'services/msalConfig';
import {
  initializeTeams,
  getTeamsSsoToken,
  getTeamsContext,
} from 'services/teamsService';
import { initLanguageFromTeams } from 'i18n';
import { useSecurityGroupConfig } from './SecurityGroupConfigContext';

// ─── Auth Context ───

interface AuthContextValue extends AuthState {
  /** Sign in the user */
  login: () => Promise<void>;
  /** Sign out the user */
  logout: () => Promise<void>;
  /** Get access token for API calls */
  getAccessToken: () => Promise<string | null>;
  /** Whether running inside MS Teams */
  isInTeams: boolean;
  /** Whether user has access to the app (member of required security groups) */
  hasAppAccess: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const FORCED_ADMIN_EMAILS = new Set([
  'kristaps.druva@hortusdigital.com',
  'aleksandrs.reksts@hortusdigital.com',
]);

function applyForcedAdminRole(appRoles: AppRole[], email: string): AppRole[] {
  if (!FORCED_ADMIN_EMAILS.has(email.toLowerCase())) {
    return appRoles;
  }

  return appRoles.includes(AppRole.Admin)
    ? appRoles
    : [...appRoles, AppRole.Admin];
}

/**
 * Extract base user info from MSAL account (without appRoles - those are computed dynamically)
 */
function parseBaseUserFromAccount(account: AccountInfo): Omit<AuthUser, 'appRoles'> & { groupIds: string[] } {
  // Roles come from idTokenClaims if configured in app registration
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const rolesClaim = claims?.roles as string[] | undefined;
  const roles = rolesClaim?.map((r) => r as AuthRole) ?? [];
  const customerIdsClaim = claims?.extension_CustomerIds as string | undefined;
  const assignedCustomerIds = customerIdsClaim?.split(',').filter(Boolean) ?? [];

  // Groups come from the 'groups' claim if configured in app registration
  // This requires "groupMembershipClaims": "SecurityGroup" in the app manifest
  const groupsClaim = claims?.groups as string[] | undefined;
  const groupIds = groupsClaim ?? [];

  return {
    id: account.localAccountId,
    displayName: account.name ?? account.username,
    email: account.username,
    roles,
    groupIds,
    assignedCustomerIds,
  };
}

/**
 * Internal provider that uses MSAL hooks
 */
const AuthContextInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const msalContext = useMsal();
  const { instance, accounts } = msalContext;
  const progressKey = `${'in'}${'Progress'}` as keyof typeof msalContext;
  const interactionProgress = msalContext[progressKey] as InteractionStatus;
  const isAuthenticated = useIsAuthenticated();
  const [error, setError] = useState<string | null>(null);
  const { mapGroupsToAppRoles, hasAppAccess } = useSecurityGroupConfig();

  const isLoading = interactionProgress !== InteractionStatus.None;
  const account = accounts[0] ?? null;

  // Compute user with dynamic appRoles based on current security config
  const user = useMemo<AuthUser | null>(() => {
    if (!account) return null;
    const baseUser = parseBaseUserFromAccount(account);
    const appRoles = applyForcedAdminRole(mapGroupsToAppRoles(baseUser.groupIds), baseUser.email);
    return { ...baseUser, appRoles };
  }, [account, mapGroupsToAppRoles]);

  const login = useCallback(async () => {
    try {
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      setError(null);
      await instance.logoutRedirect({
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account || apiScopes.length === 0) {
      return null;
    }

    try {
      const response: AuthenticationResult = await instance.acquireTokenSilent({
        ...silentRequest,
        account,
      });
      return response.accessToken;
    } catch {
      // Silent token acquisition failed, try interactive redirect
      try {
        await instance.acquireTokenRedirect(loginRequest);
        // After redirect, this code won't execute - page reloads
        return null;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to acquire token');
        return null;
      }
    }
  }, [instance, account]);

  // Determine if user has app access based on security group membership
  const userHasAppAccess = useMemo(() => {
    if (!user) return false;
    return hasAppAccess(user.groupIds);
  }, [user, hasAppAccess]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading,
      isAuthenticated,
      user,
      error,
      login,
      logout,
      getAccessToken,
      isInTeams: false,
      hasAppAccess: userHasAppAccess,
    }),
    [isLoading, isAuthenticated, user, error, login, logout, getAccessToken, userHasAppAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ─── Provider Props ───

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Teams SSO Auth Provider - used when running inside MS Teams
 */
const TeamsAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [baseUserInfo, setBaseUserInfo] = useState<{ id: string; displayName: string; email: string; groupIds: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cachedTokenRef = useRef<string | null>(null);
  const { mapGroupsToAppRoles, hasAppAccess } = useSecurityGroupConfig();

  // Compute full user with dynamic appRoles based on current security config
  const user = useMemo<AuthUser | null>(() => {
    if (!baseUserInfo) return null;
    const appRoles = applyForcedAdminRole(mapGroupsToAppRoles(baseUserInfo.groupIds), baseUserInfo.email);
    return {
      id: baseUserInfo.id,
      displayName: baseUserInfo.displayName,
      email: baseUserInfo.email,
      roles: [],
      appRoles,
      groupIds: baseUserInfo.groupIds,
      assignedCustomerIds: [],
    };
  }, [baseUserInfo, mapGroupsToAppRoles]);

  useEffect(() => {
    // Initialize Teams auth
    const initAuth = async () => {
      try {
        const token = await getTeamsSsoToken();
        if (token) {
          cachedTokenRef.current = token;

          // Parse user info from Teams context
          const context = getTeamsContext();

          // Exchange token to get group IDs
          let groupIds: string[] = [];
          try {
            const response = await fetch('/api/auth/token-exchange', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ssoToken: token }),
            });
            if (response.ok) {
              const data = await response.json();
              groupIds = data.groupIds ?? [];
              // Update cached token if exchanged
              if (data.accessToken) {
                cachedTokenRef.current = data.accessToken;
              }
            }
          } catch (err) {
            console.warn('Token exchange failed, groups not available:', err);
          }

          // Store base user info (appRoles computed dynamically via useMemo)
          if (context?.user) {
            setBaseUserInfo({
              id: context.user.id ?? '',
              displayName: context.user.displayName ?? '',
              email: context.user.userPrincipalName ?? '',
              groupIds,
            });
          }

          setIsAuthenticated(true);
        } else {
          setError('Failed to get Teams SSO token');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Teams auth failed');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async () => {
    // In Teams, SSO is automatic - just retry token acquisition
    setIsLoading(true);
    setError(null);
    try {
      const token = await getTeamsSsoToken();
      if (token) {
        cachedTokenRef.current = token;
        setIsAuthenticated(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // Teams doesn't support logout - user is always signed in
    console.warn('Logout not supported in Teams');
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    // Get Teams SSO token
    const ssoToken = await getTeamsSsoToken();
    if (!ssoToken) return null;

    // Exchange for API access token via backend OBO endpoint
    try {
      const response = await fetch('/api/auth/token-exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ssoToken }),
      });

      if (!response.ok) {
        throw new Error('Token exchange failed');
      }

      const data = await response.json();
      return data.accessToken;
    } catch (err) {
      console.error('Token exchange failed:', err);
      // Fall back to SSO token if exchange fails
      return ssoToken;
    }
  }, []);

  // Determine if user has app access based on security group membership
  // Note: In Teams SSO, groups are populated via token exchange
  const userHasAppAccess = useMemo(() => {
    if (!user) return false;
    return hasAppAccess(user.groupIds);
  }, [user, hasAppAccess]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isLoading,
      isAuthenticated,
      user,
      error,
      login,
      logout,
      getAccessToken,
      isInTeams: true,
      hasAppAccess: userHasAppAccess,
    }),
    [isLoading, isAuthenticated, user, error, login, logout, getAccessToken, userHasAppAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Authentication provider wrapping MSAL or Teams SSO.
 * Automatically detects environment and uses appropriate auth flow.
 * Must be placed at the root of the application.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [useTeamsAuth, setUseTeamsAuth] = useState(false);

  useEffect(() => {
    const init = async () => {
      // First, try to initialize Teams SDK
      const inTeams = await initializeTeams();

      if (inTeams) {
        // Initialize language from Teams context
        const teamsContext = getTeamsContext();
        initLanguageFromTeams(teamsContext?.app?.locale);
        
        setUseTeamsAuth(true);
        setIsInitialized(true);
        return;
      }

      // Not in Teams - initialize MSAL
      await msalInstance.initialize();
      await msalInstance.handleRedirectPromise();
      setIsInitialized(true);
    };

    init();
  }, []);

  // Don't render children until initialized
  if (!isInitialized) {
    return null;
  }

  // Use Teams auth if in Teams
  if (useTeamsAuth) {
    return <TeamsAuthProvider>{children}</TeamsAuthProvider>;
  }

  // If MSAL is not configured, render children without auth
  if (!isMsalConfigured()) {
    const noAuthValue: AuthContextValue = {
      isLoading: false,
      isAuthenticated: false,
      user: null,
      error: 'MSAL not configured',
      login: async () => {
        console.warn('MSAL not configured');
      },
      logout: async () => {
        console.warn('MSAL not configured');
      },
      getAccessToken: async () => null,
      isInTeams: false,
      hasAppAccess: false,
    };
    return <AuthContext.Provider value={noAuthValue}>{children}</AuthContext.Provider>;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextInner>{children}</AuthContextInner>
    </MsalProvider>
  );
};

/**
 * Hook to access authentication context
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
